import React from "react";
import Center from "../common/components/Center";
import VerticalCenter from "../common/components/VerticalCenter";

import { selectListenerProfile } from "../features/listenerProfile/select";
import { useDispatch, useSelector, useStore } from "react-redux";
import { RootState } from "../app/Store";
import {
  getEraName,
  getGenreName,
} from "../features/acoustics/EraGenreMood/EraGenreMood";
import CreditsCard from "../common/components/CreditsCard";
import { useEffect, useState } from "react";
import { whatTheme } from "../features/application/ApplicationReducer";
import PillSingleSelect from "../common/components/PillSingleSelect";
import Padding from "../common/components/Padding";
import {
  LISTENER_PROFILE_MAPPINGS,
  getSelectedValues,
} from "../features/listenerProfile/mappings";
import { loadStoryDetails } from "../features/storyDetails/StoryDetailsReducer";
import { StoryResult } from "../features/StoryInfoProxy";

function buildShareText(
  color: string,
  soothingSound: string,
  threeThings: string[],
  changeType: string,
  changeHowMuch: string,
  eraName: string,
  genreName: string,
  stories: StoryResult[]
): string {
  const desire1 = threeThings[0] ?? "";
  const desire2 = threeThings[1] ?? "";

  const record = [
    "🎵 My REMIXED Playlist",
    "",
    "Record of Change:",
    `Today I was feeling ${color} and soothed by the sound of ${soothingSound}. ` +
      (desire1 ? `I was craving ${desire1} and ` : "I was ") +
      `most drawn to making ${changeType}.`,
    "",
    `I have experienced ${changeHowMuch} in my life, and I remembered listening to ${genreName} ` +
      `from the ${eraName} when I was younger.`,
    ...(desire2 ? ["", `I was also craving ${desire2}.`] : []),
    "",
    "Playlist:",
  ];

  stories.forEach((story, i) => {
    if ("error" in story) {
      record.push(
        `${i + 1}. [Story unavailable (id: ${story.trackId}) — ${story.error}]`
      );
    } else {
      record.push(`${i + 1}. ${story.name} — ${story.authorRole}`);
      record.push(
        `   ${story.recordingCity}, ${story.recordingProvince} • ${story.recordingYear}`
      );
      record.push(`   "${story.quote}"`);
    }
    record.push("");
  });

  record.push("Experience REMIXED at remixedstories.ca");
  return record.join("\n");
}

function ActionCard({
  icon,
  label,
  href,
  onClick,
}: {
  icon: React.ReactNode;
  label: string;
  href?: string;
  onClick?: () => void;
}) {
  const style = {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    gap: "16px",
    backgroundColor: "var(--card-background)",
    border: "none",
    borderRadius: "16px",
    padding: "24px 16px",
    cursor: "pointer",
    color: "var(--green-1)",
    textAlign: "center" as const,
    fontSize: "0.9rem",
    boxShadow: "none",
    textDecoration: "none",
    textTransform: "none" as const,
  };

  if (href) {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" style={style}>
        {icon}
        {label}
      </a>
    );
  }

  return (
    <a
      href="#"
      style={style}
      onClick={(e) => {
        e.preventDefault();
        onClick?.();
      }}
    >
      {icon}
      {label}
    </a>
  );
}

export default function Credits() {
  const state = useStore().getState() as RootState;
  const listenerProfile = selectListenerProfile(state);

  // Read story details from the Redux store — populated by the saga
  const storyDetails = useSelector(
    (state: RootState) => state.storyDetails.stories
  );

  const selectedThreeThings = getSelectedValues(
    LISTENER_PROFILE_MAPPINGS.wantThreeThings,
    listenerProfile.wantThreeThings
  );

  const eraName = getEraName(listenerProfile.eraYoung);
  const genreName = getGenreName(
    listenerProfile.eraYoung,
    listenerProfile.genreYoung
  );

  const [seededChange, setSeededChange] = useState<boolean | null>(null);
  const [shareCopied, setShareCopied] = useState(false);

  const dispatch = useDispatch();

  const handleShare = async () => {
    const text = buildShareText(
      listenerProfile.color,
      LISTENER_PROFILE_MAPPINGS.soothingSound[
        listenerProfile.soothingSound - 1
      ],
      selectedThreeThings,
      LISTENER_PROFILE_MAPPINGS.changeType[listenerProfile.changeType - 1],
      LISTENER_PROFILE_MAPPINGS.changeHowMuch[listenerProfile.changeHowMuch],
      eraName,
      genreName,
      storyDetails
    );

    // navigator.share opens the native OS share sheet on mobile
    if (navigator.share) {
      await navigator.share({ title: "My REMIXED Playlist", text });
    } else {
      // Desktop fallback: copy to clipboard and briefly show "Copied!"
      await navigator.clipboard.writeText(text);
      setShareCopied(true);
      setTimeout(() => setShareCopied(false), 2000);
    }
  };

  useEffect(() => {
    dispatch(whatTheme("Init"));
    // Trigger the saga — it will fetch showpath + story details and put results in the store
    dispatch(loadStoryDetails());
  }, []);

  return (
    <>
      <img
        src="/assets/images/credits-flower-bg.webp"
        alt=""
        aria-hidden="true"
        style={{
          position: "fixed",
          bottom: 0,
          left: "50%",
          transform: "translateX(-50%)",
          width: "100%",
          pointerEvents: "none",
          zIndex: 0,
          maskImage: "linear-gradient(to bottom, transparent, black 6%)",
          WebkitMaskImage: "linear-gradient(to bottom, transparent, black 6%)",
        }}
      />
      <div
        style={{
          position: "relative",
          zIndex: 1,
          width: "100%",
          flex: 1,
          display: "flex",
          flexDirection: "column",
          gap: "var(--size-6)",
        }}
      >
        <Center>
          <img
            src="/assets/images/REMIXED logo_white.png"
            alt="REMIXED logo"
            className="logo-image"
          />
        </Center>
        <Center>
          <h2 style={{ color: "var(--green-1)" }}>
            Thanks for joining us today!
          </h2>
        </Center>
        <VerticalCenter>
          <div
            style={{ display: "flex", flexDirection: "column", gap: "16px" }}
          >
            <CreditsCard
              title="Your Record of Change"
              content={
                <>
                  <p style={{ marginTop: 0 }}>
                    Today you were feeling <u>{listenerProfile.color}</u> and
                    soothed by the sound of{" "}
                    <u>
                      {
                        LISTENER_PROFILE_MAPPINGS.soothingSound[
                          listenerProfile.soothingSound - 1
                        ]
                      }
                    </u>
                    .{" "}
                    {selectedThreeThings[0] && (
                      <>
                        You were craving <u>{selectedThreeThings[0]}</u> and{" "}
                      </>
                    )}
                    were most drawn to making{" "}
                    <u>
                      {
                        LISTENER_PROFILE_MAPPINGS.changeType[
                          listenerProfile.changeType - 1
                        ]
                      }
                    </u>
                    .
                  </p>
                  <p>
                    You have experienced{" "}
                    <u>
                      {
                        LISTENER_PROFILE_MAPPINGS.changeHowMuch[
                          listenerProfile.changeHowMuch
                        ]
                      }
                    </u>{" "}
                    in your life, and you remembered listening to{" "}
                    <u>{genreName}</u> from the <u>{eraName}</u> when you were
                    younger.
                  </p>
                  {selectedThreeThings[1] && (
                    <p style={{ marginBottom: 0 }}>
                      You were also craving <u>{selectedThreeThings[1]}</u>.
                    </p>
                  )}
                </>
              }
            />
            <CreditsCard
              title="Your Playlist"
              content={
                <div style={{ padding: "16px 16px" }}>
                  {storyDetails.map((story, i) => (
                    <div key={i}>
                      {"error" in story ? (
                        <div style={{ opacity: 0.5 }}>
                          <span style={{ color: "var(--green-1)" }}>
                            {String(i + 1).padStart(2, "0")} -{" "}
                          </span>
                          <em>
                            Story unavailable (id: {story.trackId}) —{" "}
                            {story.error}
                          </em>
                        </div>
                      ) : (
                        <>
                          <div>
                            <span style={{ color: "var(--green-1)" }}>
                              {String(i + 1).padStart(2, "0")} -{" "}
                            </span>
                            {story.authorRole}
                          </div>
                          <div>
                            <strong>
                              {story.recordingCity} {story.recordingProvince} (
                              {story.recordingYear})
                            </strong>
                          </div>
                          <div style={{ marginTop: "16px" }}>
                            <em>"{story.quote}"</em>
                          </div>
                        </>
                      )}
                      {i < storyDetails.length - 1 && (
                        <hr style={{ margin: "16px 0", opacity: 0.3 }} />
                      )}
                    </div>
                  ))}
                </div>
              }
            />
          </div>
          <Padding>
            <Center>
              <h2 style={{ color: "var(--green-1)" }}>
                Would you like to seed some change?
              </h2>
            </Center>
          </Padding>
          <Padding>
            <Center>
              <PillSingleSelect
                defaultSelectedValue={-1}
                options={[
                  { name: "YES", value: 1 },
                  { name: "NOT TODAY", value: 0 },
                ]}
                onClick={(value) => setSeededChange(value === 1)}
              />
            </Center>
          </Padding>
          {seededChange === false && (
            <>
              <Center>
                <h3 style={{ marginTop: "16px", marginBottom: "4px" }}>
                  No worries!
                </h3>
                <h3 style={{ marginBottom: "16px" }}>Let's stay in touch</h3>
              </Center>
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  padding: "0 16px",
                  margin: "32px 0",
                }}
              >
                <ActionCard
                  href="https://www.thisistrophy.com/"
                  label="Visit our website"
                  icon={
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  }
                />
                <ActionCard
                  href="https://www.facebook.com/ThisIsTrophy/"
                  label="Follow us on Facebook"
                  icon={
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 2h-3a5 5 0 0 0-5 5v3H7v4h3v8h4v-8h3l1-4h-4V7a1 1 0 0 1 1-1h3z" />
                    </svg>
                  }
                />
                <ActionCard
                  href="https://www.instagram.com/thisistrophy/?hl=en"
                  label="Follow us on Instagram"
                  icon={
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <rect x="2" y="2" width="20" height="20" rx="5" ry="5" />
                      <circle cx="12" cy="12" r="4" />
                      <circle
                        cx="17.5"
                        cy="6.5"
                        r="1"
                        fill="currentColor"
                        stroke="none"
                      />
                    </svg>
                  }
                />
              </div>
            </>
          )}
          {seededChange === true && (
            <>
              <Center>
                <h3 style={{ marginTop: "16px" }}>We're so glad!</h3>
                <h3 style={{ marginBottom: "16px" }}> Would you like to:</h3>
              </Center>
              <div
                style={{
                  display: "flex",
                  gap: "16px",
                  padding: "0 16px",
                  margin: "32px 0",
                }}
              >
                <ActionCard
                  href="https://www.thisistrophy.com/copy-of-contact-sample-page"
                  label="Contribute a story of change to our archive"
                  icon={
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                      <polyline points="15 3 21 3 21 9" />
                      <line x1="10" y1="14" x2="21" y2="3" />
                    </svg>
                  }
                />
                <ActionCard
                  label={
                    shareCopied
                      ? "Copied to clipboard!"
                      : "Share on social media"
                  }
                  onClick={handleShare}
                  icon={
                    <svg
                      width="40"
                      height="40"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <circle cx="18" cy="5" r="3" />
                      <circle cx="6" cy="12" r="3" />
                      <circle cx="18" cy="19" r="3" />
                      <line x1="8.59" y1="13.51" x2="15.42" y2="17.49" />
                      <line x1="15.41" y1="6.51" x2="8.59" y2="10.49" />
                    </svg>
                  }
                />
              </div>
            </>
          )}
        </VerticalCenter>
      </div>
    </>
  );
}
